import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Popup from 'reactjs-popup';
import { SeatsioSeatingChart } from '@seatsio/seatsio-react';
import usePageColorScheme from '../../shared/hooks/usePageColorScheme';
import UpgradePaymentFields, {
  upgradeExpectedTotal, upgradePaymentMethod, upgradeReady, upgradeRefundAmount, useUpgradeQuote,
} from './UpgradePaymentFields';

const SeatReassignmentModal = ({ ticket, seating, paymentMethods, currency, submitting, onClose, onSubmit, onPreview, canRefund }) => {
  const [picked, setPicked] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [refundDifference, setRefundDifference] = useState(false);
  const colorScheme = usePageColorScheme();

  const pickedCategory = picked && picked.category && String(picked.category.key);
  const changesTicketType = Boolean(ticket && pickedCategory && pickedCategory !== ticket.seatingCategoryKey);
  const quote = useUpgradeQuote(Boolean(picked), picked && picked.label, onPreview);

  if (!ticket || !seating) return null;

  const newTicketTypeName = changesTicketType && seating.ticketTypeNamesByCategory[pickedCategory];
  const ready = Boolean(picked) && upgradeReady(true, quote, paymentMethod);

  const submit = (event) => {
    event.preventDefault();
    if (!ready || submitting) return;
    onSubmit(picked.label, upgradePaymentMethod(quote, paymentMethod), upgradeExpectedTotal(quote, paymentMethod), upgradeRefundAmount(quote, refundDifference));
  };

  return (
    <Popup open onClose={onClose} modal closeOnDocumentClick className="popup-modal">
      <form className="op-modal" onSubmit={submit}>
        <h2 className="op-section-title">Change seat</h2>
        <p className="op-muted">Current seat: {ticket.seatAssignment}</p>

        <div className="op-seat-chart">
          <SeatsioSeatingChart
            workspaceKey={seating.publicKey}
            event={seating.eventKey}
            region="na"
            maxSelectedObjects={1}
            colorScheme={colorScheme}
            availableCategories={Object.keys(seating.ticketTypeNamesByCategory)}
            onObjectSelected={(object) => { setPicked(object); setPaymentMethod(null); setRefundDifference(false); }}
            onObjectDeselected={() => setPicked(null)}
          />
        </div>

        {picked && <p>Move to {picked.label}</p>}
        {changesTicketType && <p className="op-muted">Ticket type will change to {newTicketTypeName}.</p>}
        {picked && (
          <UpgradePaymentFields
            changesTicketType
            quote={quote}
            paidAmount={ticket.finalCost}
            currency={currency}
            paymentMethods={paymentMethods}
            paymentMethod={paymentMethod}
            onChange={setPaymentMethod}
            canRefund={canRefund}
            refundDifference={refundDifference}
            onRefundDifferenceChange={setRefundDifference}
          />
        )}

        <div className="op-modal-actions">
          <button type="button" className="op-btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="op-btn op-btn-primary" disabled={!ready || submitting}>
            {submitting ? 'Changing…' : 'Change seat'}
          </button>
        </div>
      </form>
    </Popup>
  );
};

SeatReassignmentModal.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.number.isRequired,
    seatAssignment: PropTypes.string,
    seatingCategoryKey: PropTypes.string,
    finalCost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  seating: PropTypes.shape({
    publicKey: PropTypes.string,
    eventKey: PropTypes.string.isRequired,
    ticketTypeNamesByCategory: PropTypes.objectOf(PropTypes.string).isRequired,
  }),
  paymentMethods: PropTypes.arrayOf(PropTypes.string),
  currency: PropTypes.string,
  submitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
  canRefund: PropTypes.bool,
};

SeatReassignmentModal.defaultProps = {
  currency: null,
  paymentMethods: [],
  ticket: null,
  seating: null,
  submitting: false,
  onPreview: null,
  canRefund: false,
};

export default SeatReassignmentModal;
